import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fireDB } from "../../funcs/firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { Button, Card, Spin, message, Avatar, Divider, Alert, Collapse, Tag, Typography, Space } from "antd";
import { InstagramOutlined, CheckCircleOutlined, UserOutlined, SafetyCertificateOutlined, SafetyOutlined, QuestionCircleOutlined, LockOutlined } from "@ant-design/icons";
import { jwtDecode } from "jwt-decode";

const { Text, Title, Link } = Typography;

export default function CollabInvite() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [inviteData, setInviteData] = useState(null);
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);
    const [processing, setProcessing] = useState(false);

    // Instagram OAuth Params (Reused from standard login)
    let scopes = "business_basic,business_content_publish,business_manage_comments,business_manage_messages";
    let appId = "996086435858506";
    let redirectURL = "https://app.instantdm.com/fb-rdr";
    let authUrl = `https://www.instagram.com/oauth/authorize?client_id=${appId}&redirect_uri=${redirectURL}&response_type=code&scope=${scopes}`;

    useEffect(() => {
        // Check for logged-in user via Token
        const token = localStorage.getItem("token");
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setUser(decoded);
            } catch (error) {
                console.error("Error decoding token:", error);
                // Optionally clear invalid token
                // localStorage.removeItem("token");
            }
        }

        // Fetch Invite Data
        const fetchInvite = async () => {
            try {
                const docRef = doc(fireDB, "collab_invitations", id);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    setInviteData({ id: docSnap.id, ...docSnap.data() });
                } else {
                    setError("Invitation not found or expired.");
                }
            } catch (err) {
                console.error("Error fetching invite:", err);
                setError("Failed to load invitation.");
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchInvite();
    }, [id]);

    const handleApprove = async () => {
        if (!user || !inviteData) return;

        try {
            setProcessing(true);
            const docRef = doc(fireDB, "collab_invitations", id);

            await updateDoc(docRef, {
                status: "accepted",
                accepted_at: serverTimestamp(),
                // You might want to store who accepted it if it differs slightly or for audit
                accepted_by_uid: user.user_id
            });

            message.success("Access approved successfully!");

            // Redirect to dashboard or relevant page after short delay
            setTimeout(() => {
                navigate("/dashboard");
            }, 1500);

        } catch (err) {
            console.error("Error approving invite:", err);
            message.error("Failed to approve access.");
        } finally {
            setProcessing(false);
        }
    };

    const handleLogin = () => {
        // Store current URL to redirect back after login if needed, 
        // though standard flow might just go to dashboard. 
        // For better UX, you'd want to handle the redirect back here.
        // For now, simple login redirect:
        // Store the pending invite ID so we can show the popup after login/onboarding
        localStorage.setItem("pending_collab_invite_id", id);
        window.location.href = authUrl;
    };


    if (loading) {
        return (
            <div style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center", background: "#f0f2f5" }}>
                <Spin size="large" tip="Loading invitation..." />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center", background: "#f0f2f5" }}>
                <Card style={{ maxWidth: 400, textAlign: "center" }}>
                    <div style={{ fontSize: 48, color: "#ff4d4f", marginBottom: 16 }}>⚠️</div>
                    <h2>Invitation Error</h2>
                    <p>{error}</p>
                    <Button type="primary" onClick={() => navigate("/")}>Go Home</Button>
                </Card>
            </div>
        );
    }

    const { sender, post, owner_username, status } = inviteData;
    const isOwner = user?.default_page?.username === owner_username || user?.username === owner_username;
    // Note: owner_username in inviteData comes from the username field of the post iterator. 
    // The logged in user object might have 'username' or 'name'. Adjust matching logic as per your data structure.

    return (
        <div style={{ minHeight: "100vh", background: "#f0f2f5", display: "flex", flexDirection: "column", alignItems: "center", paddingTop: "60px", paddingBottom: "40px" }}>

            {/* Logo area */}
            <div style={{ marginBottom: "30px" }}>
                <img src="/logo192.png" alt="InstantDM" style={{ height: '60px', width: '60px' }} />
            </div>

            <Card style={{ width: "100%", maxWidth: 480, borderRadius: 16, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>

                <div style={{ textAlign: "center", marginBottom: 24 }}>
                    <Avatar
                        src={sender?.picture}
                        icon={<UserOutlined />}
                        size={64}
                        style={{ border: "2px solid #1890ff" }}
                        onError={() => true} // simple handler, though pure CSS/conditional render is better
                    />
                    {/* Conditionally render avatar only if valid? Or keep fallback icon */}

                    <h2 style={{ marginTop: 16, marginBottom: 8, fontSize: '20px' }}>
                        @{sender?.username || "Someone"} requested access to manage a post
                    </h2>
                    <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', padding: '12px', borderRadius: '8px', marginTop: '12px' }}>
                        <p style={{ margin: 0, color: '#389e0d', fontSize: '14px' }}>
                            <SafetyCertificateOutlined style={{ marginRight: 8 }} />
                            <b>@{sender?.username || "The Sender"}</b> trusts InstantDM.
                        </p>
                        <p style={{ margin: '4px 0 0 0', color: '#555', fontSize: '13px' }}>
                            We use official Meta APIs to ensure your account remains 100% safe and secure.
                        </p>
                    </div>
                </div>

                <div style={{ marginBottom: 24, borderRadius: 12, overflow: 'hidden' }}>
                    <iframe
                        width="100%"
                        height="250"
                        src="https://www.youtube.com/embed/dQw4w9WgXcQ?si=DemoVideoID"
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        style={{ borderRadius: 12 }}
                    ></iframe>
                </div>

                {post && (
                    <div style={{ background: "#fafafa", borderRadius: 12, padding: 12, marginBottom: 24, border: "1px solid #eee" }}>
                        <div style={{ display: "flex", gap: 12 }}>
                            <img
                                src={post.image}
                                alt="Post"
                                style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8, flexShrink: 0 }}
                            />
                            <div style={{ overflow: "hidden" }}>
                                <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>@{post.username}</p>
                                <p style={{
                                    margin: "4px 0 0 0",
                                    color: "#555",
                                    fontSize: 13,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                }}>
                                    {post.caption || "No caption"}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {status === 'accepted' ? (
                    <Alert
                        message="Invitation Accepted"
                        description="You have successfully approved access for this post."
                        type="success"
                        showIcon
                        style={{ textAlign: "left" }}
                        action={
                            <Button size="small" type="text" onClick={() => navigate("/dashboard")}>
                                Go to Dashboard
                            </Button>
                        }
                    />
                ) : (
                    <>
                        {!user ? (
                            <div>
                                <Alert
                                    message="Authentication Required"
                                    description={`Please login as @${owner_username} to approve access.`}
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 16, textAlign: "left" }}
                                />
                                <Button
                                    type="primary"
                                    block
                                    size="large"
                                    icon={<InstagramOutlined />}
                                    onClick={handleLogin}
                                    style={{ background: "#E1306C", borderColor: "transparent" }}
                                >
                                    Login with Instagram
                                </Button>
                            </div>
                        ) : (
                            <div>
                                {/* Ideally we check if the logged in user IS the owner. 
                                    Since we rely on username strings which might be mutable or vary, 
                                    this is a "soft" check. Real permissioning happens via the OAuth token 
                                    and backend validation if strictly enforced. 
                                */}
                                <div style={{ marginBottom: 16, textAlign: "center", background: "#f9f9f9", padding: 12, borderRadius: 8 }}>
                                    Logged in as <b>@{user?.default_page?.username || user?.username}</b>
                                </div>

                                {isOwner ? (
                                    <Button
                                        type="primary"
                                        block
                                        size="large"
                                        onClick={() => {
                                            // Redirect to manage-post page to review request
                                            // We pass invite_id so that page can fetch and show the review modal
                                            window.location.href = `/manage-post/${post.id}?invite_id=${id}&action=review`;
                                        }}
                                        loading={processing}
                                        icon={<CheckCircleOutlined />}
                                    >
                                        Review Request
                                    </Button>
                                ) : (
                                    <div>
                                        <Alert
                                            message="Account Mismatch"
                                            description={`You are logged in as @${user?.default_page?.username || user?.username}, but this invite is for @${owner_username}.`}
                                            type="warning"
                                            showIcon
                                            style={{ marginBottom: 16, textAlign: "left" }}
                                        />
                                        <Button danger block onClick={() => {
                                            localStorage.clear();
                                            window.location.reload();
                                        }}>
                                            Logout & Switch Account
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

            </Card>


            <Space direction="vertical" align="center" style={{ marginTop: 24, maxWidth: 480, width: '100%' }}>

                {/* Partners Images */}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 10 }}>
                    <img style={{ width: "80px", opacity: 0.8 }} src="https://framerusercontent.com/images/c0o1mNbHLKhdEqHalSsh1qOJ8.webp?width=50&height=50" alt="Meta Partner" />
                    <img style={{ width: "100px", opacity: 0.8 }} src="https://framerusercontent.com/images/bMqRQ11HdH2UK8insn4F75SijY.jpeg?width=50&height=50" alt="Reviews" />
                </div>

                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>Trusted by 30,000+ Creators. Official Partners with:</Text>
                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Tag icon={<SafetyCertificateOutlined />} color="success">Approved by Meta</Tag>
                        <Tag color="geekblue">Shopify Partner</Tag>
                        <Tag color="purple">Make.com Partner</Tag>
                    </div>
                </div>

                {/* Account Safety FAQs */}
                <Card
                    title={<Space><SafetyOutlined style={{ color: '#52c41a' }} /> Account Safety & FAQs</Space>}
                    style={{ width: '100%', borderRadius: 16 }}
                    size="small"
                >
                    <Collapse ghost accordion>
                        <Collapse.Panel header="What is InstantDM?" key="0">
                            <p>InstantDM is a simplified, powerful alternative to tools like ManyChat. We help creators automate their Instagram DMs and comments to grow their business. We are safe, trustworthy, and Meta-approved, ensuring your account is always compliant and secure.</p>
                        </Collapse.Panel>
                        <Collapse.Panel header="Is my account safe?" key="1">
                            <p>Yes, InstantDM is 100% safe. We are an official Meta Tech Partner and use approved APIs. We never ask for your password and cannot access your private data outside of what is needed for automation.</p>
                        </Collapse.Panel>
                        <Collapse.Panel header="What permissions do I need to give?" key="2">
                            <p>The brand can never see the conversation happened between you and your audience. You can limit the permissions to only see the automation configuration or edit access to automation and view the comments username(Public data only). You can revoke this permission at any time.</p>
                        </Collapse.Panel>
                        <Collapse.Panel header="Can I revoke access later?" key="3">
                            <p>Absolutely. You are in full control. You can revoke InstantDM's access at any time directly from your Facebook or Instagram Business Integration settings.</p>
                        </Collapse.Panel>
                    </Collapse>
                </Card>

            </Space>

            <div style={{ marginTop: 40, color: "#aaa", fontSize: 12, textAlign: 'center' }}>
                <p>Protected by SSL. 256-bit Encryption.</p>
                &copy; {new Date().getFullYear()} InstantDM. All rights reserved.
            </div>
        </div >
    )
}
